/* eslint-disable max-lines-per-function */
import { useState } from 'react'
import RangeInput from '../range-input'
import useSyncForm from '@/hooks/use-sync-form'
import HorizontalNavigation from '@/stories/components/Common/HorizontalNavigation'
import Text from '@/stories/components/Common/NewText'
import {
  getDollarReturns,
  getFinalReturns,
  getStockReturns,
} from '@/root/utils/calculators/all-formulas'
import { formatNumberToINRCommaSeparatedString } from '@/root/utils/common/numbers/format-number-to-string'
import {
  DurationType,
  TUSL2Calc,
} from '@/revamp/pods/us-stocks/l2/types/page-api-response'

type TProps = {
  minAmount?: number
  maxAmount?: number
  stepGap?: number
  companyName?: string
  calcData?: TUSL2Calc
}

const ReturnCalculator = (props: TProps) => {
  const { minAmount, maxAmount, stepGap, companyName, calcData } = props
  const [durations, setDurations] = useState<Array<DurationType>>(
    calcData?.durations ?? [],
  )

  const [selectedValue, setSelectedValue] = useState<DurationType>(
    calcData?.durations?.[0] ?? {},
  )

  const { register, watch } = useSyncForm(
    {
      defaultValues: {
        amountSlider: 100000,
      },
    },
    [['amountSlider']],
  )

  const onSelectDuration = (value: string) => {
    const selectDur = durations.find(item => item.title === value)
    if (selectDur) {
      setSelectedValue(selectDur)
      setDurations(prevStat => {
        return prevStat.map(item => ({
          ...item,
          selected: item.title === value,
        }))
      })
    }
  }

  const { amountSlider } = watch()

  const stockReturns = getStockReturns(
    selectedValue.val ?? 1,
    calcData?.curr_val ?? 1,
    Number(amountSlider),
    selectedValue.val_usd ?? 1,
  )
  const dollarReturns = getDollarReturns(
    Number(amountSlider),
    selectedValue.val_usd ?? 1,
    calcData?.curr_val_usd ?? 1,
  )

  const totalCurrentValue =
    Number(amountSlider) + stockReturns.actualValue + dollarReturns.actualValue
  const totalReturns = getFinalReturns(totalCurrentValue, Number(amountSlider))

  return (
    <div className="border-brand-grey-light mx-auto flex w-full max-w-5xl flex-col items-center gap-8 rounded-xl border-[0.5px] bg-white px-6 py-8 md:px-14 md:py-10">
      <div className="flex w-full flex-col items-start gap-3">
        <div className="flex w-full flex-col items-start">
          <Text appearance="subtitle1">Investment Value</Text>
          <Text appearance="heading2">
            <span>
              {' '}
              {formatNumberToINRCommaSeparatedString(Number(amountSlider), 0)}
            </span>
          </Text>
        </div>
        <RangeInput
          {...register('amountSlider')}
          min={minAmount}
          max={maxAmount}
          step={stepGap}
        />
      </div>

      <div className="w-full xl:w-1/3">
        <HorizontalNavigation
          data={durations.map(({ selected = false, title = '' }) => ({
            selected,
            title,
          }))}
          onSelect={onSelectDuration}
        />
      </div>

      <Text appearance="body1" className="text-center text-sm">
        {companyName} investment value today
      </Text>

      <div className="flex w-full flex-col items-center justify-between gap-2 md:flex-row md:gap-0 ">
        <div className="flex w-full flex-col items-center gap-1">
          <Text appearance="subtitle1">Current value as on today</Text>
          <Text appearance="heading1">
            {formatNumberToINRCommaSeparatedString(totalCurrentValue, 0)}
          </Text>
        </div>
        <div className="flex w-full flex-col items-center gap-1">
          <Text appearance="subtitle1">Returns</Text>
          <div className="flex gap-2">
            <Text appearance="heading1" addClassName={totalReturns.color}>
              {formatNumberToINRCommaSeparatedString(totalReturns.value, 0)}
            </Text>
            <Text appearance="heading2" addClassName={totalReturns.color}>
              {totalReturns.percent}
            </Text>
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col gap-[12px]">
        <div className="border-brand-grey-light flex flex-row justify-between border-b-[0.5px] pb-4">
          <Text appearance="body1">Returns from {companyName} Stock</Text>
          <Text
            appearance="subtitle1"
            addClassName={`${stockReturns.color} text-end`}
          >
            {formatNumberToINRCommaSeparatedString(stockReturns.value, 0)}{' '}
            {stockReturns.percent}
          </Text>
        </div>
        <div className="pb- border-brand-grey-light flex flex-row justify-between border-b-[0.5px] pb-4">
          <Text appearance="body1">Dollar Returns*</Text>
          <Text
            appearance="subtitle1"
            addClassName={`${dollarReturns.color} text-end`}
          >
            {formatNumberToINRCommaSeparatedString(dollarReturns.value, 0)}{' '}
            {dollarReturns.percent}
          </Text>
        </div>
      </div>
    </div>
  )
}

export default ReturnCalculator
